import React, { useState } from 'react';
import { Holding } from '../types';
import { useLanguage } from '../context/LanguageContext';

interface Props {
  holdings: Holding[];
}

export const MacroStressTestEngine: React.FC<Props> = ({ holdings }) => {
  const { language } = useLanguage();
  const [selectedScenario, setSelectedScenario] = useState<'inflation' | 'tech_crash' | 'geopolitical' | 'rate_hike'>('inflation');

  const totalValue = holdings.reduce((sum, h) => sum + (h.quantity * (h.current_price || h.avg_cost)), 0);

  // Scenario multipliers
  const scenarios = {
    inflation: {
      name: language === 'sk' ? '🔥 Inflačný Šok & Rast Sadzieb' : '🔥 Inflation Shock & Rate Hikes',
      dropPct: -14.2,
      impactVal: totalValue * -0.142,
      resilientAsset: 'CSG / LMT (Defense & Commodities)',
      vulnerableAsset: 'WDC / Tech Growth',
      description: language === 'sk' 
        ? 'Simuluje 8% stagfláciu, zvyšovanie úrokových sadzieb ECB/FED a pokles násobkov ocenenia rastových akcií.' 
        : 'Simulates 8% stagflation, central bank rate hikes, and growth stock valuation multiple compression.'
    },
    tech_crash: {
      name: language === 'sk' ? '📉 Výpredaj Technologického Sektora (-28%)' : '📉 Tech Growth Selloff (-28%)',
      dropPct: -28.5,
      impactVal: totalValue * -0.285,
      resilientAsset: 'Cash / High Dividend Value',
      vulnerableAsset: 'WDC / NVDA / Tech',
      description: language === 'sk' 
        ? 'Prudká korekcia ocenenia AI a semiconductor technologických spoločností z historických maxím.' 
        : 'Sharp valuation correction in AI, hardware, and semiconductor stocks from historical highs.'
    },
    geopolitical: {
      name: language === 'sk' ? '⚔️ Geopolitická Kríza & Ropa' : '⚔️ Geopolitical Escalation & Oil Peak',
      dropPct: -8.7,
      impactVal: totalValue * -0.087,
      resilientAsset: 'CSG (Defense) & Gold',
      vulnerableAsset: 'Consumer Discretionary',
      description: language === 'sk' 
        ? 'Eskalácia konfliktov prináša nárast cien komodít, no obranné akcie ako CSG dosahujú rekordný Alpha nárast.' 
        : 'Escalating military conflicts spike energy prices while defense stocks achieve record Alpha gains.'
    },
    rate_hike: {
      name: language === 'sk' ? '🏦 Banková Liquidity Crunch' : '🏦 Banking Liquidity Crunch',
      dropPct: -18.4,
      impactVal: totalValue * -0.184,
      resilientAsset: 'Short-duration Treasuries / Cash',
      vulnerableAsset: 'Financials & Highly Leveraged Firms',
      description: language === 'sk' 
        ? 'Sprísnenie úverových podmienok a únik vkladov z regionálnych bánk.' 
        : 'Credit tightening and deposit flight across regional banking sector.'
    }
  };

  const current = scenarios[selectedScenario];
  const postStressVal = Math.max(0, totalValue + current.impactVal);

  return (
    <div className="glass-panel p-6 space-y-5 font-mono">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 pb-4 border-b border-[var(--border-color)]">
        <div>
          <h3 className="text-sm font-extrabold uppercase tracking-widest text-slate-900 flex items-center gap-2">
            <span>📉</span>
            {language === 'sk' ? 'Stresový Test Krízových Scenárov (Macro Stress Engine)' : 'Real-Time Macro Stress-Test Engine'}
          </h3>
          <p className="text-[10px] text-slate-500 mt-0.5">
            {language === 'sk' ? 'Simulácia dopadu makroekonomických kríz na hodnotu vášho portfólia' : 'Simulate impact of major economic shocks on your specific assets'}
          </p>
        </div>

        <div className="text-right">
          <span className="text-[9px] uppercase font-bold text-slate-500 block">
            {language === 'sk' ? 'Hodnota po Stresovom Teste' : 'Post-Stress Portfolio Value'}
          </span>
          <span className="text-xl font-black text-rose-600">
            €{postStressVal.toLocaleString(undefined, { maximumFractionDigits: 0 })}
          </span>
        </div>
      </div>

      {/* Scenario Pills */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {(Object.keys(scenarios) as Array<keyof typeof scenarios>).map((key) => {
          const sc = scenarios[key];
          const isSelected = selectedScenario === key;
          return (
            <button
              key={key}
              onClick={() => setSelectedScenario(key)}
              className={`p-3 rounded-2xl border text-left transition-all ${
                isSelected 
                  ? 'bg-slate-900 text-white border-slate-900 shadow-md scale-[1.01]' 
                  : 'bg-slate-50 text-slate-700 border-slate-200 hover:border-slate-400'
              }`}
            >
              <div className="text-[10px] font-extrabold truncate">{sc.name}</div>
              <div className={`text-xs font-black mt-1 ${isSelected ? 'text-rose-400' : 'text-rose-600'}`}>
                {sc.dropPct}% ({language === 'sk' ? 'dopad' : 'impact'})
              </div>
            </button>
          );
        })}
      </div>

      {/* Scenario Detail Card */}
      <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4 space-y-3 text-xs">
        <div className="flex justify-between items-center pb-2 border-b border-slate-200">
          <span className="font-extrabold text-slate-900">{current.name}</span>
          <span className="font-mono font-black text-rose-600">
            -€{Math.abs(current.impactVal).toLocaleString(undefined, { maximumFractionDigits: 0 })}
          </span>
        </div>
        <p className="text-[11px] text-slate-600 leading-relaxed">{current.description}</p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 text-[10px]">
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-2.5 text-emerald-800">
            <span className="font-extrabold uppercase block mb-0.5">🛡️ {language === 'sk' ? 'Odolné Aktívum' : 'Resilient Asset'}</span>
            <span>{current.resilientAsset}</span>
          </div>
          <div className="bg-rose-50 border border-rose-200 rounded-xl p-2.5 text-rose-800">
            <span className="font-extrabold uppercase block mb-0.5">⚠️ {language === 'sk' ? 'Zraniteľné Aktívum' : 'Vulnerable Asset'}</span>
            <span>{current.vulnerableAsset}</span>
          </div>
        </div>
      </div>
    </div>
  );
};
